import React, { useState, useEffect, useCallback } from 'react';
import {
  getSystemStatus, getErrors, getErrorStats, resolveError,
  getUsers, getUserDetail, updateUser, toggleUserStatus, getGlobalStats,
  approveUser, rejectUser, getUserActivity, getUserMetrics, deleteUser,
  toggleOpportunities,
} from '../services/adminApi';
import {
  Shield, RefreshCw, CheckCircle, ChevronDown, ChevronUp, Database, Server,
  Users, Car, Bug, Search, X, BarChart3, Eye, UserCog, Ban, ShieldCheck,
  TrendingUp, ShoppingCart, Tag, Send, Clock, CheckCircle2, XCircle,
  Monitor, Smartphone, Globe, LogIn, MousePointer, Settings, Activity, Trash2,
  Sparkles,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Constants                                                             */
/* ═══════════════════════════════════════════════════════════════════════ */

const TABS = [
  { key: 'monitoring', label: 'Monitoring', icon: Bug },
  { key: 'users', label: 'Utilisateurs', icon: Users },
  { key: 'stats', label: 'Statistiques', icon: BarChart3 },
];

const LEVELS = ['error', 'critical', 'warning'];
const PERIODS = [
  { label: '1h', value: 1 },
  { label: '6h', value: 6 },
  { label: '24h', value: 24 },
  { label: '7j', value: 168 },
  { label: 'Tout', value: null },
];

const levelColors = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  error: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
};
const levelDot = {
  critical: 'bg-red-500',
  error: 'bg-orange-500',
  warning: 'bg-yellow-500',
};

const planColors = {
  free: 'bg-gray-500/20 text-secondary border-gray-500/30',
  pro: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  business: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Main Admin component                                                  */
/* ═══════════════════════════════════════════════════════════════════════ */

const Admin = () => {
  const [activeTab, setActiveTab] = useState('monitoring');
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);

  // monitoring state
  const [status, setStatus] = useState(null);
  const [errors, setErrors] = useState([]);
  const [errTotal, setErrTotal] = useState(0);
  const [errStats, setErrStats] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [level, setLevel] = useState('');
  const [source, setSource] = useState('');
  const [sinceHours, setSinceHours] = useState(24);
  const [showResolved, setShowResolved] = useState(false);
  const [errPage, setErrPage] = useState(0);
  const errLimit = 30;

  // users state
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersSearchInput, setUsersSearchInput] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);
  const usersLimit = 30;

  // stats state
  const [globalStats, setGlobalStats] = useState(null);

  /* ── Fetch monitoring ──────────────────────────────────────────────── */

  const fetchMonitoring = useCallback(async () => {
    try {
      const [statusRes, errorsRes, statsRes] = await Promise.all([
        getSystemStatus(),
        getErrors({
          skip: errPage * errLimit,
          limit: errLimit,
          ...(level && { level }),
          ...(source && { source }),
          ...(sinceHours && { since_hours: sinceHours }),
          resolved: showResolved ? undefined : false,
        }),
        getErrorStats(7),
      ]);
      setStatus(statusRes.data);
      setErrors(errorsRes.data.errors);
      setErrTotal(errorsRes.data.total);
      setErrStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 403) setForbidden(true);
    } finally {
      setLoading(false);
    }
  }, [level, source, sinceHours, showResolved, errPage]);

  /* ── Fetch users ───────────────────────────────────────────────────── */

  const fetchUsers = useCallback(async () => {
    try {
      const res = await getUsers({
        skip: usersPage * usersLimit,
        limit: usersLimit,
        ...(usersSearch && { search: usersSearch }),
      });
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
    } catch (err) {
      if (err.response?.status === 403) setForbidden(true);
    }
  }, [usersPage, usersSearch]);

  /* ── Fetch stats ───────────────────────────────────────────────────── */

  const fetchStats = useCallback(async () => {
    try {
      const res = await getGlobalStats();
      setGlobalStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) setForbidden(true);
    }
  }, []);

  /* ── Effects ───────────────────────────────────────────────────────── */

  useEffect(() => {
    fetchMonitoring();
    const iv = setInterval(fetchMonitoring, 30000);
    return () => clearInterval(iv);
  }, [fetchMonitoring]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
  }, [activeTab, fetchUsers]);

  useEffect(() => {
    if (activeTab === 'stats') fetchStats();
  }, [activeTab, fetchStats]);

  /* ── Handlers ──────────────────────────────────────────────────────── */

  const handleResolve = async (id) => {
    await resolveError(id);
    fetchMonitoring();
  };

  const handleRefresh = () => {
    if (activeTab === 'monitoring') fetchMonitoring();
    else if (activeTab === 'users') fetchUsers();
    else fetchStats();
  };

  const openUserDetail = async (userId) => {
    setSelectedUser(userId);
    setUserDetailLoading(true);
    try {
      const res = await getUserDetail(userId);
      setUserDetail(res.data);
    } catch {
      setUserDetail(null);
    } finally {
      setUserDetailLoading(false);
    }
  };

  const handleUpdateUser = async (userId, data) => {
    await updateUser(userId, data);
    fetchUsers();
    if (selectedUser === userId) openUserDetail(userId);
  };

  const handleToggleStatus = async (userId) => {
    await toggleUserStatus(userId);
    fetchUsers();
    if (selectedUser === userId) openUserDetail(userId);
  };

  const handleToggleOpportunities = async (userId, action) => {
    try {
      await toggleOpportunities(userId, action);
      fetchUsers();
      if (selectedUser === userId) openUserDetail(userId);
    } catch (err) {
      console.error('Failed to toggle opportunities:', err);
    }
  };

  const handleApprove = async (userId) => {
    await approveUser(userId);
    fetchUsers();
    if (selectedUser === userId) openUserDetail(userId);
  };

  const handleReject = async (userId) => {
    await rejectUser(userId);
    fetchUsers();
    if (selectedUser === userId) openUserDetail(userId);
  };

  const handleDeleteUser = async (userId) => {
    await deleteUser(userId);
    fetchUsers();
    if (selectedUser === userId) {
      setSelectedUser(null);
      setUserDetail(null);
    }
  };

  const handleUsersSearch = (e) => {
    e.preventDefault();
    setUsersSearch(usersSearchInput);
    setUsersPage(0);
  };

  /* ── Guards ────────────────────────────────────────────────────────── */

  if (forbidden) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-primary mb-2">Accès refusé</h2>
          <p className="text-secondary">Vous n'avez pas les droits admin.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="h-8 w-8 text-carmin animate-spin" />
      </div>
    );
  }

  /* ── Render ────────────────────────────────────────────────────────── */

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-7 w-7 text-carmin" />
          <h1 className="text-2xl font-bold text-primary">Admin</h1>
        </div>
        <button onClick={handleRefresh} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-dark-surface border border-dark-border text-secondary hover:text-primary transition-colors">
          <RefreshCw className="h-4 w-4" />
          Rafraîchir
        </button>
      </div>

      {/* Tabs */}
      <div className="flex rounded-xl border border-dark-border overflow-hidden">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-carmin text-white'
                  : 'bg-dark-surface text-secondary hover:text-primary'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'monitoring' && (
        <MonitoringTab
          status={status}
          errors={errors}
          total={errTotal}
          stats={errStats}
          expandedId={expandedId}
          setExpandedId={setExpandedId}
          level={level}
          setLevel={setLevel}
          source={source}
          setSource={setSource}
          sinceHours={sinceHours}
          setSinceHours={setSinceHours}
          showResolved={showResolved}
          setShowResolved={setShowResolved}
          page={errPage}
          setPage={setErrPage}
          limit={errLimit}
          onResolve={handleResolve}
        />
      )}

      {activeTab === 'users' && (
        <UsersTab
          users={users}
          total={usersTotal}
          page={usersPage}
          setPage={setUsersPage}
          limit={usersLimit}
          searchInput={usersSearchInput}
          setSearchInput={setUsersSearchInput}
          onSearch={handleUsersSearch}
          selectedUser={selectedUser}
          userDetail={userDetail}
          userDetailLoading={userDetailLoading}
          onOpenDetail={openUserDetail}
          onCloseDetail={() => { setSelectedUser(null); setUserDetail(null); }}
          onUpdateUser={handleUpdateUser}
          onToggleStatus={handleToggleStatus}
          onApprove={handleApprove}
          onReject={handleReject}
          onDelete={handleDeleteUser}
          onToggleOpportunities={handleToggleOpportunities}
        />
      )}

      {activeTab === 'stats' && (
        <StatsTab stats={globalStats} />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Monitoring Tab (existing logic extracted)                              */
/* ═══════════════════════════════════════════════════════════════════════ */

const MonitoringTab = ({
  status, errors, total, stats, expandedId, setExpandedId,
  level, setLevel, source, setSource, sinceHours, setSinceHours,
  showResolved, setShowResolved, page, setPage, limit, onResolve,
}) => {
  const sources = stats ? Object.keys(stats.by_source) : [];

  return (
    <div className="space-y-6">
      {/* Health bar */}
      {status && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <HealthCard icon={Database} label="MongoDB" ok={status.mongodb} />
          <HealthCard icon={Server} label="Redis" ok={status.redis} />
          <HealthCard icon={Users} label="Utilisateurs" value={status.users_count} />
          <HealthCard icon={Car} label="Véhicules" value={status.vehicles_count} />
          <HealthCard icon={Bug} label="Erreurs (1h)" value={status.recent_errors_1h} alert={status.recent_errors_1h > 0} />
        </div>
      )}

      {/* Stats summary */}
      {stats && (
        <div className="bg-dark-surface rounded-xl border border-dark-border p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="text-sm text-secondary">
              Non résolues : <span className="text-primary font-bold text-lg">{stats.unresolved}</span>
            </div>
            <div className="h-6 w-px bg-dark-border" />
            {Object.entries(stats.by_level).map(([lv, count]) => (
              <span key={lv} className={`px-2.5 py-1 rounded-full text-xs font-medium border ${levelColors[lv] || 'bg-gray-500/20 text-secondary border-gray-500/30'}`}>
                {lv}: {count}
              </span>
            ))}
            <div className="h-6 w-px bg-dark-border" />
            <div className="flex items-center gap-2 text-xs text-muted">
              7j :
              {Object.entries(stats.by_day).map(([day, count]) => (
                <span key={day} className="bg-dark-border px-1.5 py-0.5 rounded text-secondary">
                  {day.slice(5)} ({count})
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select value={level} onChange={(e) => { setLevel(e.target.value); setPage(0); }} className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-primary">
          <option value="">Tous levels</option>
          {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
        </select>

        <select value={source} onChange={(e) => { setSource(e.target.value); setPage(0); }} className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-primary">
          <option value="">Toutes sources</option>
          {sources.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <div className="flex rounded-lg border border-dark-border overflow-hidden">
          {PERIODS.map(p => (
            <button
              key={p.label}
              onClick={() => { setSinceHours(p.value); setPage(0); }}
              className={`px-3 py-2 text-xs font-medium transition-colors ${sinceHours === p.value ? 'bg-carmin text-white' : 'bg-dark-surface text-secondary hover:text-primary'}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm text-secondary cursor-pointer">
          <input type="checkbox" checked={showResolved} onChange={(e) => { setShowResolved(e.target.checked); setPage(0); }} className="rounded bg-dark-surface border-dark-border" />
          Voir résolues
        </label>
      </div>

      {/* Error list */}
      <div className="space-y-2">
        {errors.length === 0 && (
          <div className="bg-dark-surface rounded-xl border border-dark-border p-8 text-center text-muted">
            <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500/50" />
            Aucune erreur trouvée
          </div>
        )}

        {errors.map((err) => (
          <div key={err.id} className={`bg-dark-surface rounded-xl border ${err.resolved ? 'border-dark-border/50 opacity-60' : 'border-dark-border'}`}>
            <div
              className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-dark-hover/10 transition-colors"
              onClick={() => setExpandedId(expandedId === err.id ? null : err.id)}
            >
              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${levelDot[err.level] || 'bg-gray-500'}`} />
              <span className="text-xs text-muted font-mono w-36 flex-shrink-0">
                {new Date(err.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
              <span className="text-xs px-2 py-0.5 rounded bg-dark-border text-secondary flex-shrink-0">{err.source}</span>
              <span className={`text-xs px-2 py-0.5 rounded border ${levelColors[err.level] || ''} flex-shrink-0`}>{err.level}</span>
              <span className="text-sm text-label truncate flex-1">{err.message}</span>
              {err.resolved && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
              {expandedId === err.id ? <ChevronUp className="h-4 w-4 text-muted" /> : <ChevronDown className="h-4 w-4 text-muted" />}
            </div>

            {expandedId === err.id && (
              <div className="px-4 pb-4 border-t border-dark-border pt-3 space-y-3">
                {err.stack_trace && (
                  <pre className="text-xs text-secondary bg-black/30 rounded-lg p-3 overflow-x-auto max-h-60 whitespace-pre-wrap">{err.stack_trace}</pre>
                )}
                {err.user_id && (
                  <p className="text-xs text-muted">User ID : <span className="text-secondary font-mono">{err.user_id}</span></p>
                )}
                {Object.keys(err.metadata || {}).length > 0 && (
                  <div className="text-xs text-muted">
                    Metadata : <code className="text-secondary">{JSON.stringify(err.metadata)}</code>
                  </div>
                )}
                {err.resolved && err.resolved_at && (
                  <p className="text-xs text-green-500/70">
                    Résolu le {new Date(err.resolved_at).toLocaleString('fr-FR')} par {err.resolved_by}
                  </p>
                )}
                {!err.resolved && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onResolve(err.id); }}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 transition-colors text-sm"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Marquer résolu
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {total > limit && (
        <Pagination page={page} setPage={setPage} total={total} limit={limit} />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Users Tab                                                             */
/* ═══════════════════════════════════════════════════════════════════════ */

const UsersTab = ({
  users, total, page, setPage, limit,
  searchInput, setSearchInput, onSearch,
  selectedUser, userDetail, userDetailLoading,
  onOpenDetail, onCloseDetail, onUpdateUser, onToggleStatus,
  onApprove, onReject, onDelete, onToggleOpportunities,
}) => {
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  return (
    <div className="space-y-4">
      {/* Search */}
      <form onSubmit={onSearch} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Rechercher par email ou nom..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-dark-surface border border-dark-border text-primary text-sm placeholder-muted focus:outline-none focus:border-carmin"
          />
        </div>
        <button type="submit" className="px-4 py-2 rounded-lg bg-carmin text-white text-sm font-medium hover:bg-carmin/90 transition-colors">
          Rechercher
        </button>
      </form>

      {/* Users table */}
      <div className="bg-dark-surface rounded-xl border border-dark-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-border text-muted text-left">
                <th className="px-4 py-3 font-medium">Utilisateur</th>
                <th className="px-4 py-3 font-medium">Plan</th>
                <th className="px-4 py-3 font-medium text-center">Véhicules</th>
                <th className="px-4 py-3 font-medium text-center">Pubs</th>
                <th className="px-4 py-3 font-medium text-center">Listings</th>
                <th className="px-4 py-3 font-medium">Inscription</th>
                <th className="px-4 py-3 font-medium">Statut</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted">Aucun utilisateur trouvé</td>
                </tr>
              )}
              {users.map((u) => (
                <tr key={u.id} className="border-b border-dark-border/50 hover:bg-dark-hover/10 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-primary">{u.name}</div>
                    <div className="text-xs text-muted">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={u.plan}
                      onChange={(e) => onUpdateUser(u.id, { plan: e.target.value })}
                      className="bg-transparent border border-dark-border rounded px-2 py-1 text-xs text-primary cursor-pointer focus:outline-none focus:border-carmin"
                    >
                      <option value="free">free</option>
                      <option value="pro">pro</option>
                      <option value="business">business</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-center text-label">{u.vehicles_count}</td>
                  <td className="px-4 py-3 text-center text-label">{u.publications_count}</td>
                  <td className="px-4 py-3 text-center text-label">{u.listings_count}</td>
                  <td className="px-4 py-3 text-xs text-secondary">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {u.disabled ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-500/20 text-red-400 border border-red-500/30">désactivé</span>
                    ) : !u.approved ? (
                      <div className="flex items-center gap-1">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-400 border border-amber-500/30">en attente</span>
                        <button onClick={() => onApprove(u.id)} className="p-1 rounded text-green-400 hover:bg-green-500/10 transition-colors" title="Approuver">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => onReject(u.id)} className="p-1 rounded text-red-400 hover:bg-red-500/10 transition-colors" title="Rejeter">
                          <XCircle className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : u.is_admin ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-carmin/20 text-carmin border border-carmin/30">admin</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-500/20 text-green-400 border border-green-500/30">actif</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onOpenDetail(u.id)}
                        className="p-1.5 rounded-lg text-muted hover:text-primary hover:bg-dark-hover/20 transition-colors"
                        title="Voir détails"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onUpdateUser(u.id, { is_admin: !u.is_admin })}
                        className={`p-1.5 rounded-lg transition-colors ${u.is_admin ? 'text-carmin hover:bg-carmin/10' : 'text-muted hover:text-primary hover:bg-dark-hover/20'}`}
                        title={u.is_admin ? 'Retirer admin' : 'Rendre admin'}
                      >
                        <ShieldCheck className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onToggleStatus(u.id)}
                        className={`p-1.5 rounded-lg transition-colors ${u.disabled ? 'text-green-400 hover:bg-green-500/10' : 'text-muted hover:text-red-400 hover:bg-red-500/10'}`}
                        title={u.disabled ? 'Activer' : 'Désactiver'}
                      >
                        <Ban className="h-4 w-4" />
                      </button>
                      {!u.is_admin && (
                        confirmDeleteId === u.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => { onDelete(u.id); setConfirmDeleteId(null); }}
                              className="px-2 py-0.5 rounded text-[10px] font-semibold bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
                            >
                              Confirmer
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="p-1 rounded text-muted hover:text-primary transition-colors"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmDeleteId(u.id)}
                            className="p-1.5 rounded-lg text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {total > limit && (
        <Pagination page={page} setPage={setPage} total={total} limit={limit} />
      )}

      {/* User detail modal */}
      {selectedUser && (
        <UserDetailModal
          detail={userDetail}
          loading={userDetailLoading}
          onClose={onCloseDetail}
          onApprove={onApprove}
          onReject={onReject}
          onToggleOpportunities={onToggleOpportunities}
        />
      )}
    </div>
  );
};

/* ── User detail modal ───────────────────────────────────────────────── */

const UserDetailModal = ({ detail, loading, onClose, onApprove, onReject, onToggleOpportunities }) => {
  const [tab, setTab] = useState('overview');
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState('');
  const [activityPage, setActivityPage] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const userId = detail?.user?.id;

  const fetchActivity = useCallback(async () => {
    if (!userId) return;
    setActivityLoading(true);
    try {
      const params = { skip: activityPage * 30, limit: 30 };
      if (activityFilter) params.action = activityFilter;
      const res = await getUserActivity(userId, params);
      if (activityPage === 0) {
        setActivity(res.data);
      } else {
        setActivity(prev => prev ? { ...res.data, activities: [...prev.activities, ...res.data.activities] } : res.data);
      }
    } catch { setActivity(null); }
    finally { setActivityLoading(false); }
  }, [userId, activityFilter, activityPage]);

  const fetchMetrics = useCallback(async () => {
    if (!userId) return;
    setMetricsLoading(true);
    try {
      const res = await getUserMetrics(userId);
      setMetrics(res.data);
    } catch { setMetrics(null); }
    finally { setMetricsLoading(false); }
  }, [userId]);

  useEffect(() => {
    if (tab === 'activity') fetchActivity();
  }, [tab, fetchActivity]);

  useEffect(() => {
    if (tab === 'metrics') fetchMetrics();
  }, [tab, fetchMetrics]);

  const handleFilterChange = (f) => {
    setActivityFilter(f);
    setActivityPage(0);
    setActivity(null);
  };

  const timeAgo = (date) => {
    if (!date) return '—';
    const now = new Date();
    const d = new Date(date);
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'à l\'instant';
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
    if (diff < 2592000) return `il y a ${Math.floor(diff / 86400)}j`;
    return d.toLocaleDateString('fr-FR');
  };

  const actionIcon = (action) => {
    switch (action) {
      case 'login': return <LogIn className="h-3.5 w-3.5 text-blue-400" />;
      case 'register': return <ShieldCheck className="h-3.5 w-3.5 text-green-400" />;
      case 'page_view': return <MousePointer className="h-3.5 w-3.5 text-secondary" />;
      case 'create_vehicle': return <Car className="h-3.5 w-3.5 text-purple-400" />;
      case 'publish': return <Send className="h-3.5 w-3.5 text-carmin" />;
      case 'settings_update': return <Settings className="h-3.5 w-3.5 text-amber-400" />;
      default: return <Activity className="h-3.5 w-3.5 text-muted" />;
    }
  };

  const actionLabel = (action) => {
    const labels = { login: 'Connexion', register: 'Inscription', page_view: 'Page vue', create_vehicle: 'Véhicule créé', publish: 'Publication', settings_update: 'Paramètres' };
    return labels[action] || action;
  };

  const DETAIL_TABS = [
    { key: 'overview', label: 'Aperçu', icon: Eye },
    { key: 'activity', label: 'Activité', icon: Activity },
    { key: 'metrics', label: 'Métriques', icon: BarChart3 },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center pt-12 px-4" onClick={onClose}>
      <div className="bg-dark-surface border border-dark-border rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border sticky top-0 bg-dark-surface z-10">
          <h3 className="text-lg font-bold text-primary">Détail utilisateur</h3>
          <button onClick={onClose} className="p-1 text-muted hover:text-primary transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 text-carmin animate-spin" />
          </div>
        ) : !detail ? (
          <div className="px-6 py-8 text-center text-muted">Erreur de chargement</div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex border-b border-dark-border px-6">
              {DETAIL_TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      tab === t.key
                        ? 'border-carmin text-carmin'
                        : 'border-transparent text-secondary hover:text-primary'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {t.label}
                  </button>
                );
              })}
            </div>

            <div className="px-6 py-4">
              {/* ── Overview tab ───────────────────────── */}
              {tab === 'overview' && (
                <div className="space-y-5">
                  {/* Approval banner */}
                  {!detail.user.approved && !detail.user.is_admin && (
                    <div className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <Clock className="h-5 w-5 text-amber-400" />
                        <span className="text-sm text-amber-300 font-medium">Compte en attente d'approbation</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => onApprove(detail.user.id)} className="px-3 py-1.5 rounded-lg bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 text-sm font-medium transition-colors">
                          Approuver
                        </button>
                        <button onClick={() => onReject(detail.user.id)} className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 text-sm font-medium transition-colors">
                          Rejeter
                        </button>
                      </div>
                    </div>
                  )}
                  {detail.user.approved && (
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <CheckCircle className="h-4 w-4" />
                      <span>Approuvé{detail.user.approved_at ? ` le ${new Date(detail.user.approved_at).toLocaleDateString('fr-FR')}` : ''}</span>
                    </div>
                  )}

                  {/* Opportunities access */}
                  <div className="rounded-xl border border-dark-border bg-dark-card p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                          <Sparkles className="h-4 w-4 text-amber-400" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-primary">Opportunites</span>
                          {detail.user.opportunities_status === 'requested' && detail.user.opportunities_requested_at && (
                            <p className="text-xs text-muted">Demande le {new Date(detail.user.opportunities_requested_at).toLocaleDateString('fr-FR')}</p>
                          )}
                          {detail.user.opportunities_status === 'active' && detail.user.opportunities_activated_at && (
                            <p className="text-xs text-muted">Active le {new Date(detail.user.opportunities_activated_at).toLocaleDateString('fr-FR')}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${
                          detail.user.opportunities_status === 'active'
                            ? 'bg-green-500/10 text-green-400 border-green-500/30'
                            : detail.user.opportunities_status === 'requested'
                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                            : detail.user.opportunities_status === 'rejected'
                            ? 'bg-red-500/10 text-red-400 border-red-500/30'
                            : 'bg-dark-border text-muted border-dark-border'
                        }`}>
                          {detail.user.opportunities_status === 'active' ? 'Actif'
                            : detail.user.opportunities_status === 'requested' ? 'Demande'
                            : detail.user.opportunities_status === 'rejected' ? 'Rejete'
                            : detail.user.opportunities_status === 'deactivated' ? 'Desactive'
                            : 'Non demande'}
                        </span>
                        {detail.user.opportunities_status !== 'active' && (
                          <button
                            onClick={() => onToggleOpportunities(detail.user.id, 'activate')}
                            className="px-2.5 py-1 rounded-lg bg-green-500/10 text-green-400 border border-green-500/30 hover:bg-green-500/20 text-xs font-medium transition-colors"
                          >
                            Activer
                          </button>
                        )}
                        {detail.user.opportunities_status === 'requested' && (
                          <button
                            onClick={() => onToggleOpportunities(detail.user.id, 'reject')}
                            className="px-2.5 py-1 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 text-xs font-medium transition-colors"
                          >
                            Rejeter
                          </button>
                        )}
                        {detail.user.opportunities_status === 'active' && (
                          <button
                            onClick={() => onToggleOpportunities(detail.user.id, 'deactivate')}
                            className="px-2.5 py-1 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 text-xs font-medium transition-colors"
                          >
                            Desactiver
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* User info grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <InfoItem label="Nom" value={detail.user.name} />
                    <InfoItem label="Email" value={detail.user.email} />
                    <InfoItem label="Entreprise" value={detail.user.company || '—'} />
                    <InfoItem label="Plan" value={detail.user.plan} badge={planColors[detail.user.plan]} />
                    <InfoItem label="Admin" value={detail.user.is_admin ? 'Oui' : 'Non'} />
                    <InfoItem label="Statut" value={detail.user.disabled ? 'Désactivé' : 'Actif'} />
                    <InfoItem label="Inscription" value={detail.user.created_at ? new Date(detail.user.created_at).toLocaleString('fr-FR') : '—'} />
                  </div>

                  {/* Counts */}
                  <div className="grid grid-cols-4 gap-3">
                    <MiniStat label="Véhicules" value={detail.vehicles.length} icon={Car} />
                    <MiniStat label="Publications" value={detail.publications.length} icon={Send} />
                    <MiniStat label="Listings" value={detail.listings.length} icon={ShoppingCart} />
                    <MiniStat label="Offres" value={detail.offers.length} icon={Tag} />
                  </div>

                  {/* Vehicles */}
                  {detail.vehicles.length > 0 && (
                    <DetailSection title="Véhicules" count={detail.vehicles.length}>
                      {detail.vehicles.slice(0, 10).map((v) => (
                        <div key={v.id} className="flex items-center justify-between py-2 border-b border-dark-border/30 last:border-0">
                          <span className="text-sm text-label">{v.brand} {v.model} {v.year || ''}</span>
                          <div className="flex items-center gap-2">
                            {v.stock_status && <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-border text-secondary">{v.stock_status}</span>}
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-border text-secondary">{v.status || 'draft'}</span>
                          </div>
                        </div>
                      ))}
                      {detail.vehicles.length > 10 && <p className="text-xs text-muted pt-2">+ {detail.vehicles.length - 10} autres</p>}
                    </DetailSection>
                  )}

                  {/* Publications */}
                  {detail.publications.length > 0 && (
                    <DetailSection title="Publications" count={detail.publications.length}>
                      {detail.publications.slice(0, 10).map((p) => (
                        <div key={p.id} className="flex items-center justify-between py-2 border-b border-dark-border/30 last:border-0">
                          <span className="text-sm text-label">{p.platform}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.status === 'published' ? 'bg-green-500/20 text-green-400' : p.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-dark-border text-secondary'}`}>
                            {p.status}
                          </span>
                        </div>
                      ))}
                    </DetailSection>
                  )}
                </div>
              )}

              {/* ── Activity tab ───────────────────────── */}
              {tab === 'activity' && (
                <div className="space-y-4">
                  {/* Filter */}
                  <div className="flex items-center gap-2">
                    {['', 'login', 'register', 'page_view', 'create_vehicle', 'publish'].map((f) => (
                      <button
                        key={f}
                        onClick={() => handleFilterChange(f)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          activityFilter === f
                            ? 'bg-carmin text-white'
                            : 'bg-dark-border text-secondary hover:text-primary'
                        }`}
                      >
                        {f === '' ? 'Tout' : actionLabel(f)}
                      </button>
                    ))}
                  </div>

                  {activityLoading && !activity ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-5 w-5 text-carmin animate-spin" />
                    </div>
                  ) : !activity || activity.activities.length === 0 ? (
                    <div className="text-center py-8 text-muted text-sm">Aucune activité enregistrée</div>
                  ) : (
                    <>
                      <div className="text-xs text-muted mb-2">{activity.total} événements</div>
                      <div className="space-y-1">
                        {activity.activities.map((a) => (
                          <div key={a.id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-dark-hover/10 transition-colors">
                            <div className="flex-shrink-0">{actionIcon(a.action)}</div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-primary font-medium">{actionLabel(a.action)}</span>
                                {a.path && <span className="text-xs text-muted truncate">{a.path}</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              {a.country && (
                                <div className="flex items-center gap-1">
                                  <Globe className="h-3 w-3 text-muted" />
                                  <span className="text-[10px] text-secondary">{a.city ? `${a.city}, ` : ''}{a.country}</span>
                                </div>
                              )}
                              {a.device_type && (
                                <div className="flex-shrink-0">
                                  {a.device_type === 'mobile' ? <Smartphone className="h-3 w-3 text-muted" /> : <Monitor className="h-3 w-3 text-muted" />}
                                </div>
                              )}
                              <span className="text-[10px] text-muted w-20 text-right">{timeAgo(a.created_at)}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {activity.activities.length < activity.total && (
                        <button
                          onClick={() => setActivityPage(p => p + 1)}
                          disabled={activityLoading}
                          className="w-full py-2 text-sm text-secondary hover:text-primary transition-colors"
                        >
                          {activityLoading ? 'Chargement...' : 'Charger plus'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── Metrics tab ───────────────────────── */}
              {tab === 'metrics' && (
                <div className="space-y-5">
                  {metricsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-5 w-5 text-carmin animate-spin" />
                    </div>
                  ) : !metrics ? (
                    <div className="text-center py-8 text-muted text-sm">Aucune donnée</div>
                  ) : (
                    <>
                      {/* Summary cards */}
                      <div className="grid grid-cols-3 gap-3">
                        <MiniStat label="Connexions" value={metrics.login_count} icon={LogIn} />
                        <MiniStat label="Jours actifs" value={metrics.active_days} icon={Activity} />
                        <MiniStat label="Actions totales" value={metrics.total_actions} icon={MousePointer} />
                      </div>

                      {/* Last login info */}
                      {metrics.last_login && (
                        <div className="bg-black/20 rounded-xl p-4 space-y-2">
                          <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Dernière connexion</h4>
                          <div className="grid grid-cols-2 gap-3">
                            <InfoItem label="Date" value={new Date(metrics.last_login).toLocaleString('fr-FR')} />
                            <InfoItem label="IP" value={metrics.last_ip || '—'} />
                            <InfoItem label="Localisation" value={[metrics.last_city, metrics.last_country].filter(Boolean).join(', ') || '—'} />
                            <InfoItem label="Device" value={metrics.last_device || '—'} />
                          </div>
                        </div>
                      )}

                      {/* Entity counts */}
                      <div className="grid grid-cols-4 gap-3">
                        <MiniStat label="Véhicules" value={metrics.vehicles_count} icon={Car} />
                        <MiniStat label="Publications" value={metrics.publications_count} icon={Send} />
                        <MiniStat label="Listings" value={metrics.listings_count} icon={ShoppingCart} />
                        <MiniStat label="Offres" value={metrics.offers_count} icon={Tag} />
                      </div>

                      {/* Top pages */}
                      {metrics.top_pages && metrics.top_pages.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-3">Pages les plus visitées</h4>
                          <div className="space-y-2">
                            {metrics.top_pages.map((p, i) => (
                              <div key={i} className="flex items-center gap-3">
                                <span className="text-xs text-secondary w-32 truncate">{p.page}</span>
                                <div className="flex-1 h-2 bg-dark-border rounded-full overflow-hidden">
                                  <div className="h-full rounded-full bg-carmin" style={{ width: `${Math.max((p.views / (metrics.top_pages[0]?.views || 1)) * 100, 2)}%` }} />
                                </div>
                                <span className="text-xs text-secondary w-12 text-right">{p.views}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Stats Tab                                                             */
/* ═══════════════════════════════════════════════════════════════════════ */

const StatsTab = ({ stats }) => {
  if (!stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 text-carmin animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Utilisateurs" value={stats.users.total} sub={`+${stats.users.new_this_month} ce mois`} />
        <StatCard icon={Car} label="Véhicules" value={stats.vehicles.total} />
        <StatCard icon={Send} label="Publications" value={stats.publications.total} />
        <StatCard icon={ShoppingCart} label="Listings actifs" value={stats.marketplace.active_listings} />
      </div>

      {/* Users by plan */}
      <div className="bg-dark-surface rounded-xl border border-dark-border p-4">
        <h3 className="text-sm font-semibold text-primary mb-3">Utilisateurs par plan</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats.users.by_plan).map(([plan, count]) => (
            <span key={plan} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${planColors[plan] || 'bg-gray-500/20 text-secondary border-gray-500/30'}`}>
              {plan}: {count}
            </span>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Vehicles by status */}
        <div className="bg-dark-surface rounded-xl border border-dark-border p-4">
          <h3 className="text-sm font-semibold text-primary mb-3">Véhicules par status</h3>
          <div className="space-y-2">
            {Object.entries(stats.vehicles.by_status).map(([st, count]) => (
              <BarRow key={st} label={st} value={count} total={stats.vehicles.total} />
            ))}
          </div>
          {Object.keys(stats.vehicles.by_stock_status).length > 0 && (
            <>
              <h4 className="text-xs font-medium text-muted mt-4 mb-2">Stock status</h4>
              <div className="space-y-2">
                {Object.entries(stats.vehicles.by_stock_status).map(([st, count]) => (
                  <BarRow key={st} label={st} value={count} total={stats.vehicles.total} color="bg-blue-500" />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Publications breakdown */}
        <div className="bg-dark-surface rounded-xl border border-dark-border p-4">
          <h3 className="text-sm font-semibold text-primary mb-3">Publications</h3>
          <h4 className="text-xs font-medium text-muted mb-2">Par plateforme</h4>
          <div className="space-y-2 mb-4">
            {Object.entries(stats.publications.by_platform).map(([pl, count]) => (
              <BarRow key={pl} label={pl} value={count} total={stats.publications.total} color="bg-purple-500" />
            ))}
          </div>
          <h4 className="text-xs font-medium text-muted mb-2">Par statut</h4>
          <div className="space-y-2">
            {Object.entries(stats.publications.by_status).map(([st, count]) => (
              <BarRow
                key={st}
                label={st}
                value={count}
                total={stats.publications.total}
                color={st === 'published' ? 'bg-green-500' : st === 'failed' ? 'bg-red-500' : 'bg-gray-500'}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Marketplace */}
      <div className="bg-dark-surface rounded-xl border border-dark-border p-4">
        <h3 className="text-sm font-semibold text-primary mb-3">Marketplace</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Listings actifs" value={stats.marketplace.active_listings} icon={ShoppingCart} />
          <MiniStat label="Offres totales" value={stats.marketplace.total_offers} icon={Tag} />
          <MiniStat label="En attente" value={stats.marketplace.pending_offers} icon={TrendingUp} />
          <MiniStat label="Acceptées" value={stats.marketplace.accepted_offers} icon={CheckCircle} />
        </div>
      </div>

      {/* Timeline charts */}
      <div className="grid md:grid-cols-2 gap-4">
        <TimelineChart title="Inscriptions (30j)" data={stats.timeline.signups} color="bg-carmin" />
        <TimelineChart title="Publications (30j)" data={stats.timeline.publications} color="bg-purple-500" />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Shared sub-components                                                 */
/* ═══════════════════════════════════════════════════════════════════════ */

const HealthCard = ({ icon: Icon, label, ok, value, alert }) => (
  <div className={`bg-dark-surface rounded-xl border p-3 flex items-center gap-3 ${alert ? 'border-red-500/50' : 'border-dark-border'}`}>
    <Icon className={`h-5 w-5 ${ok === true ? 'text-green-500' : ok === false ? 'text-red-500' : alert ? 'text-red-400' : 'text-secondary'}`} />
    <div>
      <div className="text-xs text-muted">{label}</div>
      {ok !== undefined ? (
        <div className={`text-sm font-medium ${ok ? 'text-green-400' : 'text-red-400'}`}>
          {ok ? 'OK' : 'DOWN'}
        </div>
      ) : (
        <div className={`text-sm font-bold ${alert ? 'text-red-400' : 'text-primary'}`}>{value}</div>
      )}
    </div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-dark-surface rounded-xl border border-dark-border p-4">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-4 w-4 text-muted" />
      <span className="text-xs text-muted">{label}</span>
    </div>
    <div className="text-2xl font-bold text-primary">{value?.toLocaleString('fr-FR') ?? 0}</div>
    {sub && <div className="text-xs text-carmin mt-1">{sub}</div>}
  </div>
);

const MiniStat = ({ label, value, icon: Icon }) => (
  <div className="bg-black/20 rounded-lg p-3 text-center">
    {Icon && <Icon className="h-4 w-4 text-muted mx-auto mb-1" />}
    <div className="text-lg font-bold text-primary">{value}</div>
    <div className="text-[10px] text-muted">{label}</div>
  </div>
);

const InfoItem = ({ label, value, badge }) => (
  <div>
    <div className="text-[10px] text-muted uppercase tracking-wider mb-0.5">{label}</div>
    {badge ? (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${badge}`}>{value}</span>
    ) : (
      <div className="text-sm text-primary">{value}</div>
    )}
  </div>
);

const DetailSection = ({ title, count, children }) => (
  <div>
    <h4 className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">
      {title} ({count})
    </h4>
    <div className="bg-black/20 rounded-lg px-3">
      {children}
    </div>
  </div>
);

const BarRow = ({ label, value, total, color = 'bg-carmin' }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-secondary w-24 truncate">{label || '—'}</span>
      <div className="flex-1 h-2 bg-dark-border rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(pct, 1)}%` }} />
      </div>
      <span className="text-xs text-secondary w-16 text-right">{value} ({pct}%)</span>
    </div>
  );
};

const TimelineChart = ({ title, data, color = 'bg-carmin' }) => {
  const entries = Object.entries(data || {});
  if (entries.length === 0) {
    return (
      <div className="bg-dark-surface rounded-xl border border-dark-border p-4">
        <h3 className="text-sm font-semibold text-primary mb-3">{title}</h3>
        <p className="text-xs text-muted">Aucune donnée</p>
      </div>
    );
  }

  const maxVal = Math.max(...entries.map(([, v]) => v), 1);

  return (
    <div className="bg-dark-surface rounded-xl border border-dark-border p-4">
      <h3 className="text-sm font-semibold text-primary mb-3">{title}</h3>
      <div className="flex items-end gap-[2px] h-24">
        {entries.map(([day, count]) => (
          <div key={day} className="flex-1 flex flex-col items-center justify-end h-full group relative">
            <div
              className={`w-full rounded-t ${color} min-h-[2px]`}
              style={{ height: `${(count / maxVal) * 100}%` }}
            />
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black/80 text-primary text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap z-10">
              {day.slice(5)}: {count}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="text-[9px] text-dimmed">{entries[0]?.[0]?.slice(5)}</span>
        <span className="text-[9px] text-dimmed">{entries[entries.length - 1]?.[0]?.slice(5)}</span>
      </div>
    </div>
  );
};

const Pagination = ({ page, setPage, total, limit }) => (
  <div className="flex items-center justify-center gap-3">
    <button
      disabled={page === 0}
      onClick={() => setPage(p => p - 1)}
      className="px-4 py-2 rounded-lg bg-dark-surface border border-dark-border text-secondary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed"
    >
      Précédent
    </button>
    <span className="text-sm text-muted">
      {page * limit + 1} - {Math.min((page + 1) * limit, total)} / {total}
    </span>
    <button
      disabled={(page + 1) * limit >= total}
      onClick={() => setPage(p => p + 1)}
      className="px-4 py-2 rounded-lg bg-dark-surface border border-dark-border text-secondary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed"
    >
      Suivant
    </button>
  </div>
);

export default Admin;
